import React, { useEffect, useState, useContext } from 'react';
import DatePicker from '../../components/dateComponents/DatePicker2';
import GbitTablePaging from '../../components/tableComponents/GbitTablePaging';
import { dbPost } from '../../apis/commonApi';
import { formatDateToISOString } from '../../services/UtilService';
import { ModalContext } from '../../components/contexts';

const PageTitle = () => (
  <div className="page-title-wrap" data-type="responsive">
    <h2 className="h-tit">AI 영상 분석 결과</h2>
  </div>
);

const DateFilter = ({
  onDateChange,
  customStartDate,
  customEndDate,
  setCustomStartDate,
  setCustomEndDate,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('전체');
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const { modalService } = useContext(ModalContext);
  const [displayStartDate, setDisplayStartDate] = useState('');
  const [displayEndDate, setDisplayEndDate] = useState('');

  const formatDateForDisplay = date => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateDates = (date, comparisonDate, type) => {
    if (type === 'endDate' && !comparisonDate) {
      modalService.errorAlert('먼저 시작 날짜를 선택하세요.');
      return false;
    }

    const selectedDate = new Date(date);
    const compareDate = comparisonDate ? new Date(comparisonDate) : null;

    if (type === 'startDate' && compareDate && selectedDate > compareDate) {
      modalService.errorAlert('시작 날짜는 종료 날짜보다 이후일 수 없습니다.');
      return false;
    }

    if (type === 'endDate' && compareDate && selectedDate < compareDate) {
      modalService.errorAlert('종료 날짜는 시작 날짜보다 이전일 수 없습니다.');
      return false;
    }

    return true;
  };

  const handleStartDateChange = date => {
    const formattedDate = formatDateToISOString(date);
    const displayDate = formatDateForDisplay(date);
    if (!validateDates(formattedDate, customEndDate, 'startDate')) {
      return;
    }
    setCustomStartDate(formattedDate);
    onDateChange({ startDate: formattedDate, endDate: customEndDate });
    setDisplayStartDate(displayDate);
  };

  const handleEndDateChange = date => {
    const formattedDate = formatDateToISOString(date);
    const displayDate = formatDateForDisplay(date);
    if (!validateDates(formattedDate, customStartDate, 'endDate')) {
      return;
    }
    setCustomEndDate(formattedDate);
    onDateChange({ startDate: customStartDate, endDate: formattedDate });
    setDisplayEndDate(displayDate);
  };

  const handlePeriodSelect = label => {
    setSelectedPeriod(label);

    if (label !== '기간 조회') {
      const today = new Date();
      let startDate = null;
      let endDate = new Date();

      if (label === '3개월') {
        startDate = new Date(today.setMonth(today.getMonth() - 3));
      } else if (label === '6개월') {
        startDate = new Date(today.setMonth(today.getMonth() - 6));
      } else if (label === '1년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 1));
      } else if (label === '2년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 2));
      } else if (label === '3년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 3));
      } else if (label === '전체') {
        startDate = null;
        endDate = null;
      }

      const formattedStartDate = formatDateToISOString(startDate);
      const formattedEndDate = formatDateToISOString(endDate, true);

      onDateChange({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }
  };

  return (
    <td>
      <div className="input-group">
        {['전체', '3개월', '6개월', '1년', '2년', '3년', '기간 조회'].map(
          (label, idx) => (
            <div className="form-chip sm" key={idx}>
              <input
                type="radio"
                className="radio"
                name="rdo_chip_period"
                id={`rdo_chip_period_${idx}`}
                checked={selectedPeriod === label}
                onChange={() => handlePeriodSelect(label)}
              />
              <label
                className="form-chip-outline"
                htmlFor={`rdo_chip_period_${idx}`}
                style={{ height: 'auto' }}
              >
                {label}
              </label>
            </div>
          )
        )}
      </div>
      {selectedPeriod === '기간 조회' && (
        <div className="input-group gap-md">
          <DatePicker
            selectedDate={displayStartDate}
            onDateChange={handleStartDateChange}
            label="시작 날짜"
          />
          <DatePicker
            selectedDate={displayEndDate}
            onDateChange={handleEndDateChange}
            label="종료 날짜"
          />
        </div>
      )}
    </td>
  );
};

const ResultTable = ({ data }) => (
  <div className="tbl-wrap">
    <h3 className="block-tit">결과 현황</h3>
    <table className="tbl col data scroll">
      <caption>결과 현황 결과 테이블입니다.</caption>
      <thead>
        <tr>
          <th rowSpan={2} scope="col">
            조사 일자
          </th>
          <th rowSpan={2} scope="col">
            조사 구분
          </th>
          <th rowSpan={2} scope="col">
            조사자
          </th>
          <th rowSpan={2} scope="col">
            회사명
          </th>
          <th rowSpan={2} scope="col">
            공정명
          </th>
          <th rowSpan={2} scope="col">
            작업명
          </th>
          <th colSpan={4} scope="col">
            OWAS
          </th>
          <th colSpan={4} scope="col">
            RULA
          </th>
          <th colSpan={5} scope="col">
            REBA
          </th>
        </tr>
        <tr>
          <th scope="col">1</th>
          <th scope="col">2</th>
          <th scope="col">3</th>
          <th scope="col">4</th>
          <th scope="col">1</th>
          <th scope="col">2</th>
          <th scope="col">3</th>
          <th scope="col">4</th>
          <th scope="col">1</th>
          <th scope="col">2</th>
          <th scope="col">3</th>
          <th scope="col">4</th>
          <th scope="col">5</th>
        </tr>
      </thead>

      <tbody>
        {data.map((item, idx) => (
          <tr key={idx}>
            <td>{item.date}</td>
            <td>
              <span
                className={`krds-badge outline ${
                  item.type === '수시' ? 'sec' : ''
                }`}
              >
                {item.type}
              </span>
            </td>
            <td>
              <strong>{item.inspector}</strong>
            </td>
            <td>{item.processName}</td>
            <td>{item.taskName}</td>
            <td>{item.workName}</td>
            {item.owas.map((value, owasIdx) => (
              <td className="owas-column" key={`${owasIdx}`}>
                {value}
              </td>
            ))}
            {item.rula.map((value, rulaIdx) => (
              <td className="rula-column" key={`${rulaIdx}`}>
                {value}
              </td>
            ))}
            {item.reba.map((value, rebaIdx) => (
              <td className="reba-column" key={`${rebaIdx}`}>
                {value}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ResStats = () => {
  useEffect(() => {
    document.title = '통계 - AI 영상 분석 결과';
  }, []);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [aiEntNmList, setAiEntNmList] = useState([]);
  const [aiTaskNmList, setAiTaskNmList] = useState([]);
  const [aiTpbizNmList, setAiTpbizNmList] = useState([]);
  const [selectedEntNm, setSelectedEntNm] = useState('');
  const [selectedTaskNm, setSelectedTaskNm] = useState('');
  const [selectedTpbizNm, setSelectedTpbizNm] = useState('');
  const [filteredTaskList, setFilteredTaskList] = useState([]);
  const [selectedApplySe, setSelectedApplySe] = useState('');
  const [data, setData] = useState([
    {
      date: '2024-11-01',
      type: '정기',
      inspector: '홍길동',
      processName: '공정1',
      taskName: '작업1',
      owas: [3, 2, 4, 1],
      rula: [5, 4, 3, 2],
      reba: [3, 2, 1, 4, 5],
    },
    {
      date: '2024-12-01',
      type: '수시',
      inspector: '김철수',
      processName: '공정2',
      taskName: '작업2',
      owas: [4, 3, 2, 1],
      rula: [4, 3, 2, 1],
      reba: [2, 3, 4, 1, 5],
    },
  ]);
  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    entNm: '',
    tpbizNm: '',
    taskNm: '',
    applySe: '',
  });

  useEffect(() => {
    fetchSearchData();
  }, []);

  useEffect(() => {
    fetchData(filter);
  }, [filter]);

  const fetchData = async (params = {}) => {
    try {
      const response = await dbPost('/stats/resStats/search.do', params);
      if (response && Array.isArray(response)) {
        const formattedData = response.map(item => ({
          date: item.regDt,
          type: item.applySe === 'O' ? '수시' : '정기',
          inspector: item.applyUser,
          processName: item.entNm,
          taskName: item.taskNm,
          workName: item.workNm,
          owas: [
            item.owasScore1,
            item.owasScore2,
            item.owasScore3,
            item.owasScore4,
          ],
          rula: [
            item.rulaScore1,
            item.rulaScore2,
            item.rulaScore3,
            item.rulaScore4,
          ],
          reba: [
            item.rebaScore1,
            item.rebaScore2,
            item.rebaScore3,
            item.rebaScore4,
            item.rebaScore5,
          ],
        }));
        setData(formattedData);
      }
    } catch (error) {}
  };

  const fetchSearchData = async () => {
    try {
      const response = await dbPost('/stats/aiStats/search.do');
      if (response) {
        setAiEntNmList(response.aiEntNmList || []);
        setAiTaskNmList(response.aiTaskNmList || []);
        setAiTpbizNmList(response.aiTpbizNmList || []);
      }
    } catch (error) {}
  };

  const handleSearch = () => {
    const updatedFilter = {
      startDate: formatDateToISOString(customStartDate) || filter.startDate,
      endDate: formatDateToISOString(customEndDate, true) || filter.endDate,
      entNm: selectedEntNm,
      taskNm: selectedTaskNm,
      tpbizNm: selectedTpbizNm,
      applySe: selectedApplySe,
    };
    setFilter(updatedFilter);
  };

  const handleFilterChange = updatedFilter => {
    setFilter(prev => ({ ...prev, ...updatedFilter }));
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const paginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const handleTpbizChange = e => {
    const selectedTpbiz = e.target.value;
    setSelectedTpbizNm(selectedTpbiz);

    if (selectedTpbiz) {
      const tpbiz = aiTpbizNmList.find(item => item.tpbizNm === selectedTpbiz);
      if (tpbiz) {
        const filteredTasks = aiTaskNmList.filter(
          task => task.tpbizTypeId === tpbiz.tpbizTypeId
        );
        setFilteredTaskList(filteredTasks);
      } else {
        setFilteredTaskList([]);
      }
    } else {
      setFilteredTaskList([]);
    }

    setSelectedTaskNm('');
  };

  return (
    <div className="contents">
      <PageTitle />
      <div className="tbl-wrap">
        <table className="tbl data row">
          <caption>조회 조건 선택 테이블입니다.</caption>
          <tbody>
            <tr>
              <th scope="row">조회 기간</th>
              <DateFilter
                onDateChange={handleFilterChange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                setCustomStartDate={setCustomStartDate}
                setCustomEndDate={setCustomEndDate}
              />
            </tr>
            <tr>
              <th scope="row">조회 구분</th>
              <td>
                <div className="input-group">
                  <label className="hidden-label" htmlFor="sel1">
                    라벨
                  </label>
                  <select
                    id="sel1"
                    className="form-select md"
                    value={selectedApplySe}
                    onChange={e => setSelectedApplySe(e.target.value)}
                  >
                    <option value="">조사 구분</option>
                    <option value="A">정기</option>
                    <option value="O">수시</option>
                  </select>
                  <label className="hidden-label" htmlFor="sel2">
                    라벨
                  </label>
                  <select
                    id="sel2"
                    className="form-select md"
                    value={selectedEntNm}
                    onChange={e => setSelectedEntNm(e.target.value)}
                  >
                    <option value="">기업 선택</option>
                    {aiEntNmList.map((item, index) => (
                      <option key={index} value={item.entNm}>
                        {item.entNm}
                      </option>
                    ))}
                  </select>
                </div>
              </td>
            </tr>
            <tr>
              <th scope="row">조회 구분</th>
              <td>
                <div className="input-group">
                  <label className="hidden-label" htmlFor="sel3">
                    라벨
                  </label>
                  <select
                    id="sel3"
                    className="form-select md"
                    value={selectedTpbizNm}
                    onChange={handleTpbizChange}
                  >
                    <option value="">업종 선택</option>
                    {aiTpbizNmList.map((item, index) => (
                      <option key={index} value={item.tpbizNm}>
                        {item.tpbizNm}
                      </option>
                    ))}
                  </select>
                  <label className="hidden-label" htmlFor="sel4">
                    라벨
                  </label>
                  <select
                    id="sel4"
                    className="form-select md"
                    value={selectedTaskNm}
                    onChange={e => setSelectedTaskNm(e.target.value)}
                    disabled={!selectedTpbizNm}
                  >
                    <option value="">공정 선택</option>
                    {filteredTaskList.map((item, index) => (
                      <option key={index} value={item.taskNm}>
                        {item.taskNm}
                      </option>
                    ))}
                  </select>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap gap-lg">
        <button
          type="button"
          className="btn xlg primary"
          onClick={handleSearch}
        >
          검색하기
        </button>
      </div>
      <ResultTable data={paginatedData()} />
      <div className="pagination w-page">
        <GbitTablePaging
          totalItems={data.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default ResStats;
